import { onlyDigits } from './normalize';

export type BoletoValidationResult =
  | { kind: 'valid'; normalized: string }
  | { kind: 'thirdParty'; normalized: string }
  | { kind: 'invalid' };

export function mod10CheckDigit(digits: string): number {
  let sum = 0;
  let weight = 2;

  for (let i = digits.length - 1; i >= 0; i--) {
    let product = Number(digits[i]) * weight;

    if (product > 9) {
      product = Math.floor(product / 10) + (product % 10);
    }

    sum += product;
    weight = weight === 2 ? 1 : 2;
  }

  return (10 - (sum % 10)) % 10;
}

export function mod11CheckDigit(digits: string): number {
  let sum = 0;
  let weight = 2;

  for (let i = digits.length - 1; i >= 0; i--) {
    sum += Number(digits[i]) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }

  const remainder = sum % 11;

  if (remainder === 0 || remainder === 1) {
    return 1;
  }

  const checkDigit = 11 - remainder;

  return checkDigit === 10 ? 1 : checkDigit;
}

function validateDigitableLine(digits: string): boolean {
  const field1 = digits.slice(0, 10);
  const field2 = digits.slice(10, 21);
  const field3 = digits.slice(21, 32);
  const generalCheckDigit = digits.slice(32, 33);

  if (mod10CheckDigit(field1.slice(0, 9)) !== Number(field1[9])) {
    return false;
  }

  if (mod10CheckDigit(field2.slice(0, 10)) !== Number(field2[10])) {
    return false;
  }

  if (mod10CheckDigit(field3.slice(0, 10)) !== Number(field3[10])) {
    return false;
  }

  const bankCurrency = field1.slice(0, 4);
  const freeField = field1.slice(4, 9) + field2.slice(0, 10) + field3.slice(0, 10);
  const dueDateAndValue = digits.slice(33, 47);
  const barcodeWithoutCheckDigit = bankCurrency + dueDateAndValue + freeField;

  return mod11CheckDigit(barcodeWithoutCheckDigit) === Number(generalCheckDigit);
}

function validateBarcode(digits: string): boolean {
  const bankCurrency = digits.slice(0, 4);
  const generalCheckDigit = digits.slice(4, 5);
  const rest = digits.slice(5, 44);
  const barcodeWithoutCheckDigit = bankCurrency + rest;

  return mod11CheckDigit(barcodeWithoutCheckDigit) === Number(generalCheckDigit);
}

export function validateBoleto(rawInput: string): BoletoValidationResult {
  const normalized = onlyDigits(rawInput);

  if (normalized.length === 48 && normalized.startsWith('8')) {
    return { kind: 'thirdParty', normalized };
  }

  if (normalized.length === 47 && validateDigitableLine(normalized)) {
    return { kind: 'valid', normalized };
  }

  if (normalized.length === 44 && validateBarcode(normalized)) {
    return { kind: 'valid', normalized };
  }

  return { kind: 'invalid' };
}
